import React, { useCallback, useMemo } from 'react';
import SearchField from 'components/Field/SearchField/SearchField';
import StatusFilters from 'components/StatusFilters/StatusFilters';
import useFilters from 'hooks/useFilters';
import { getUserAllowedStatusKeys } from 'modules/get-user-allowed-status-keys/get-user-allowed-status-keys';
import WarehouseFilter from 'components/WarehouseFilter/WarehouseFilter';
import {
  isDSVStock,
  isSFPStock,
  isStock,
  isWarehouseManager,
} from 'modules/user-has-permission/user-has-permission';
import AgenciesFilters from 'components/AgenciesFilters/AgenciesFilters';
import { toggleActiveAgencies } from 'modules/toggle-active-agencies/toggle-active-agencies';
import Portal from 'components/Portal/Portal';
import FiltersBar from 'components/FiltersBar/FiltersBar';
import IsMarketplaceFilter from 'components/IsMarketplaceFilter/IsMarketplaceFilter';
import MarketplaceFilter from 'components/MarketplaceFilter/MarketplaceFilter';
import { STATUSES } from 'constants/constants';

export default function PreparationOrdersFiltersBar({
  query,
  onChangeQuery = () => {},
  onFiltersChange = () => {},
}) {
  const allowedStatusKeys = useMemo(() => getUserAllowedStatusKeys(), []);
  const userIsWarehouseManager = useMemo(() => isWarehouseManager(), []);
  const userIsStock = useMemo(() => isStock(), []);
  const userIsMarketplaceStock = useMemo(
    () => isDSVStock() || isSFPStock(),
    [],
  );

  const [filters, setFilters] = useFilters(
    'preparationOrdersFilters',
    onFiltersChange,
    {
      status: allowedStatusKeys.filter(
        (status) => status !== STATUSES.CLOSED.name,
      ),
      warehouse_id: '',
      agency_id: [],
      isMarketplace: false,
      marketplace_id: '',
    },
  );

  const toggleStatuses = useCallback(
    (status) => {
      const newActiveStatuses = filters?.status.includes(status)
        ? filters?.status.filter((activeStatus) => activeStatus !== status)
        : filters?.status.concat(status);
      setFilters({ ...filters, status: newActiveStatuses });
    },
    [filters, setFilters],
  );

  const updateWarehouse = useCallback(
    (event, { value }) => setFilters({ ...filters, warehouse_id: value }),
    [filters, setFilters],
  );

  const toggleAgencies = useCallback(
    (agency_id) => {
      const newActiveAgencies = toggleActiveAgencies(filters, agency_id);
      setFilters({ ...filters, agency_id: newActiveAgencies });
    },
    [filters, setFilters],
  );

  const updateIsMarketplace = useCallback(
    (event, { checked: is_marketplace }) => {
      setFilters({ ...filters, is_marketplace });
    },
    [filters, setFilters],
  );

  const updateMarketplace = useCallback(
    (event, { value }) => {
      setFilters({ ...filters, marketplace_id: value });
    },
    [filters, setFilters],
  );

  return (
    <FiltersBar>
      <FiltersBar.Row>
        <Portal.In target="page-header-right">
          <SearchField
            value={query}
            placeholder="Buscar…"
            onChange={onChangeQuery}
          />
        </Portal.In>
        <>
          {(userIsWarehouseManager || userIsStock) && (
            <AgenciesFilters
              filterByOrigin={userIsStock}
              activeAgencies={filters?.agency_id}
              onToggleAgency={toggleAgencies}
            />
          )}
          {!userIsStock && (
            <StatusFilters
              onToggleStatus={toggleStatuses}
              activeStatuses={filters?.status}
            />
          )}
          {(userIsWarehouseManager || userIsStock) && (
            <WarehouseFilter
              currentWarehouse={filters?.warehouse_id}
              onChangeWarehouse={updateWarehouse}
            />
          )}
          {userIsMarketplaceStock && (
            <MarketplaceFilter
              clearable
              currentMarketplace={filters?.marketplace_id}
              onChangeMarketplace={updateMarketplace}
            />
          )}
          {!userIsStock && (
            <IsMarketplaceFilter
              checked={filters?.is_marketplace}
              onToggle={updateIsMarketplace}
            />
          )}
        </>
      </FiltersBar.Row>
    </FiltersBar>
  );
}
